import { useEffect, useMemo, useState } from 'preact/hooks';
import { isActiveLog, STATE_ORDER, type LogDescriptor } from '../../core/log/client';
import { href, route } from '../../router';
import { shortLogName } from '../../core/loglist/slug';
import { logPath } from '../nav';
import { registry, registryState } from '../state/logs';
import { settings, updateSettings } from '../state/settings';
import { collapsedOperators, expandOperator, setAllOperators, toggleOperator } from '../state/groups';
import { Icon } from './Icon';
import { StateBadge } from './LogBadges';

/** Searchable list of every log, grouped by operator; keeps the current log in view. */
export function LogSidebar({ currentId, open, onClose }: { currentId?: string; open: boolean; onClose: () => void }) {
  const [q, setQ] = useState('');
  const reg = registry.value;
  const showRetired = settings.value.showRetired;
  const groups = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out: Array<[string, LogDescriptor[]]> = [];
    for (const [op, logs] of reg.byOperator()) {
      const f = logs
        .filter((l) => l.id === currentId || showRetired || isActiveLog(l))
        .filter((l) => !needle || `${l.description} ${op} ${l.url}`.toLowerCase().includes(needle))
        .sort((a, b) => STATE_ORDER.indexOf(a.state) - STATE_ORDER.indexOf(b.state) || a.description.localeCompare(b.description));
      if (f.length) out.push([op, f]);
    }
    return out;
  }, [reg, q, currentId, showRetired]);
  const hidden = reg.all().length - groups.reduce((n, [, l]) => n + l.length, 0);
  const collapsed = collapsedOperators.value;
  const searching = q.trim() !== '';
  const allCollapsed = groups.length > 0 && groups.every(([op]) => collapsed.has(op));
  // Navigating into a log reveals its group once; the user may collapse it again afterwards.
  const currentOperator = currentId ? reg.byId(currentId)?.operator : undefined;
  useEffect(() => {
    if (currentOperator) expandOperator(currentOperator);
  }, [currentId, currentOperator]);

  return (
    <aside class={`sidebar ${open ? 'open' : ''}`} aria-label="Logs">
      <div class="sidebar-head">
        <div class="row" style="gap:0.4rem">
          <input class="input input-sm" type="search" placeholder="Find a log…" value={q} onInput={(e) => setQ((e.target as HTMLInputElement).value)} aria-label="Find a log" />
          <button type="button" class="btn-icon sidebar-toggle" onClick={onClose} aria-label="Close log list">
            <Icon name="x" />
          </button>
        </div>
        {groups.length > 0 && (
          <>
            <div class="sidebar-meta">
              <span>{groups.length} operators · {groups.reduce((n, [, l]) => n + l.length, 0)} logs</span>
              <button type="button" class="linklike" onClick={() => setAllOperators(groups.map(([op]) => op), !allCollapsed)}>
                <Icon name={allCollapsed ? 'expandAll' : 'collapseAll'} size={13} /> {allCollapsed ? 'Expand all' : 'Collapse all'}
              </button>
            </div>
            <label class="check sidebar-meta" style="justify-content:flex-start;padding-top:0.3rem">
              <input type="checkbox" checked={showRetired} onChange={(e) => updateSettings({ showRetired: (e.target as HTMLInputElement).checked })} />
              include retired / rejected{hidden > 0 && !showRetired ? ` (${hidden})` : ''}
            </label>
          </>
        )}
      </div>
      <div class="sidebar-list">
        <a class={`sidebar-item sidebar-home ${route.value.path === '/' || route.value.path === '/logs' ? 'active' : ''}`} href={href('/logs')} onClick={onClose}>
          <Icon name="list" size={15} />
          <span class="name">All logs</span>
          <span class="muted tiny">overview</span>
        </a>
        {registryState.value.status === 'loading' && groups.length === 0 && <div class="sidebar-empty">Loading log lists…</div>}
        {groups.map(([op, logs]) => {
          // A search always shows matching logs.
          const isOpen = searching || !collapsed.has(op);
          return (
          <div key={op}>
            <button type="button" class={`sidebar-group ${isOpen ? 'open' : ''}`} onClick={() => toggleOperator(op)} aria-expanded={isOpen}>
              <Icon name="chevronRight" class="chev" size={13} />
              <span class="name">{op}</span>
              <span>{logs.length}</span>
            </button>
            {isOpen && logs.map((l) => (
              <a key={l.id} class={`sidebar-item ${l.id === currentId ? 'active' : ''}`} href={logPath(l)} onClick={onClose} title={l.description}>
                <span class={`kind-dot ${l.kind}`} title={l.kind === 'tiled' ? 'tiled (static-ct-api)' : 'RFC 6962'} />
                <span class="name">{shortLogName(l)}</span>
                {l.state !== 'usable' && <StateBadge state={l.state} />}
              </a>
            ))}
          </div>
          );
        })}
        {groups.length === 0 && registryState.value.status !== 'loading' && <div class="sidebar-empty">No logs match.</div>}
      </div>
    </aside>
  );
}
